import { createWithEqualityFn } from 'zustand/traditional';
import { shallow } from 'zustand/shallow';

import {
  GetChannelArchitectureAPI,
  GetSeriesAPI,
  GetTitleGroupLookupAPI,
} from 'api';

import { getMondayZeroToSixSchedules } from '../helper/getMondayZeroToSixSchedules';
import { reconstructBlockName } from '../utils/architecture.utils';

export const useProgramsStore = createWithEqualityFn(
  (set, get) => ({
    programs: {
      allPrograms: [],
      isLoading: false,
      titleGroupList: [],
      seriesList: [],
      isClockTypeBroadcast: false,
    },
    setAllPrograms: (programList) =>
      set((state) => ({
        programs: {
          ...state.programs,
          allPrograms: programList,
        },
      })),
    setProgramsIsLoading: (value) =>
      set((state) => ({
        programs: {
          ...state.programs,
          isLoading: value,
        },
      })),

    setTitleGroupList: (list) =>
      set((state) => ({
        programs: {
          ...state.programs,
          titleGroupList: list,
        },
      })),

    setSeriesList: (list) =>
      set((state) => ({
        programs: {
          ...state.programs,
          seriesList: list,
        },
      })),

    setClockTypeBroadcast: (value) =>
      set((state) => ({
        programs: {
          ...state.programs,
          isClockTypeBroadcast: value,
        },
      })),

    _resetPrograms: () =>
      set((state) => ({
        programs: {
          ...state.programs,
          isLoading: false,
          titleGroupList: [],
          seriesList: [],
          isClockTypeBroadcast: false,
        },
      })),

    _resetProgramInfoForm: () =>
      set((state) => ({
        programInfo: {
          ...state.programs,
          form: {
            availableDuration: 0,
            blockName: '',
            blockReference: '',
            dayOfWeek: 1,
            endTime: null,
            genre: '',
            layout: null,
            link: false,
            maxCount: 0,
            seasonID: null,
            seriesID: null,
            seriesName: '',
            sequential: false,
            startTime: null,
            titleGroupID: null,
            titleGroupName: '',
            type: 'Movies',
            week: 1,
            id: 0,
          },
        },
      })),

    // === Info Fetching Functions
    getSeriesInfo: (seriesName, seasonId) => {
      const { seriesList } = get().programs;
      const found = seriesList.find((s) => seriesName === s.name);
      const seasonData = found?.seasons.find(
        (season) => season.id === seasonId
      );
      return (
        found && {
          seriesID: found?.id,
          seriesName: found?.name,
          seasonID: seasonData?.id,
          seasonName: seasonData?.name,
        }
      );
    },

    getTitleGroupInfo: (titleGroupName) => {
      const { titleGroupList } = get().programs;
      const found = titleGroupList.find((t) => titleGroupName === t.name);

      return found;
    },

    // API Handler
    fetchTitleGroupList: () => {
      GetTitleGroupLookupAPI({
        onSuccess: (response) => {
          set((state) => ({
            programs: {
              ...state.programs,
              titleGroupList: response,
            },
          }));
        },
        onError: () => {
          console.error('API Error retreiving title groups list.');
        },
      });
    },

    fetchSeriesList: (channelId) => {
      GetSeriesAPI({
        queryParams: { channelId },
        onSuccess: (response) => {
          set((state) => ({
            programs: {
              ...state.programs,
              seriesList: response.series,
              isLoading: false,
            },
          }));
        },
        onError: () => {
          console.error('API Error retreiving series list.');
        },
      });
    },

    fetchChannelArchitecture: (
      channelId,
      startDate,
      endDate,
      isClockTypeBroadcast,
      callback
    ) => {
      set((state) => ({
        programs: {
          ...state.programs,
          isLoading: true,
        },
      }));

      GetChannelArchitectureAPI({
        queryParams: { channelId },
        onSuccess: (response) => {
          const all = getMondayZeroToSixSchedules(
            response,
            { startDate, endDate },
            isClockTypeBroadcast,
            false
          ).map((e) => ({
            ...e,
            blockName: reconstructBlockName(e.blockName),
            blockReference: reconstructBlockName(e.blockReference),
          }));

          set((state) => ({
            programs: {
              ...state.programs,
              allPrograms: all,
              isLoading: false,
            },
          }));

          if (typeof callback === 'function') callback(all);
        },
        onError: () => {
          set((state) => ({
            programs: {
              ...state.programs,
              isLoading: false,
            },
          }));
        },
      });
    },
  }),
  shallow
);
